// src/components/sales/sections/TeamPerformanceSection.jsx
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Lightbulb, Users, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import salesService from '../../../services/salesService';

const TeamPerformanceSection = ({ filters, onFiltersChange }) => {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const getStartDateFromFilters = (custom = null) => {
    const f = custom || filters;
    if (!f) return null;
    const refDate = new Date(f.reference_date);
    if (f.periods_back === 'all') return new Date(refDate - 2*365*864e5).toISOString().split('T')[0];
    return new Date(refDate - f.periods_back*(f.aggregation_level==='weekly'?7:30)*864e5).toISOString().split('T')[0];
  };

  const processRaw = (raw) => {
    if (!raw?.length) return [];
    const stats = {};
    raw.forEach(r => {
      const c = r.opportunity_closer || 'Unknown';
      if (!stats[c]) stats[c] = { name:c, calls:0, deals:0, appointments:0, deal_value:0, showed:0, no_show:0 };
      stats[c].calls++; stats[c].appointments++;
      if (r.deal_is_won)              stats[c].deals++;
      stats[c].deal_value += r.opportunity_value_clean || r.won_revenue || 0;
      if (r.status_category==='showed')  stats[c].showed++;
      if (r.status_category==='no_show') stats[c].no_show++;
    });
    return Object.values(stats);
  };

  const fetch = async () => {
    if (!filters) return;
    setLoading(true); setError(null);
    try {
      const start = getStartDateFromFilters();
      const end   = filters.reference_date;
      if (!start || !end) throw new Error('Invalid date range');
      const bulk = await salesService.getBulkSalesData({ start_date: start, end_date: end });
      if (bulk?.raw_data?.length) {
        const filtered = filters.closer!=='all' ? bulk.raw_data.filter(r=>r.opportunity_closer===filters.closer) : bulk.raw_data;
        setData(processRaw(filtered));
      } else setData([]);
    } catch (e) {
      setError(e.message || 'Failed to load team performance data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, [filters]);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm border border-grey-200 p-6">
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><Users className="h-4 w-4" />Calls by Closer</h3>
      <div className="flex items-center justify-center h-48 text-grey-400">
        <div className="text-center"><RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" /><p>Loading team performance…</p></div>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error) return (
    <div className="bg-white rounded-xl shadow-sm border border-grey-200 p-6">
      <h3 className="text-sm font-semibold text-darkblue-500 mb-4 flex items-center gap-2"><Users className="h-4 w-4" />Calls by Closer</h3>
      <div className="flex items-center justify-center h-48 text-pink-600">
        <div className="text-center"><AlertCircle className="h-8 w-8 mx-auto mb-2" /><p>{error}</p>
          <button onClick={fetch} className="mt-2 text-xs text-brightblue-600 hover:text-brightblue-700 underline">Retry</button>
        </div>
      </div>
    </div>
  );

  // ── Empty ────────────────────────────────────────────────────────────────────
  if (!data?.length) return (
    <div className="bg-white rounded-xl shadow-sm border border-grey-200 p-6">
      <h3 className="text-sm font-semibold text-darkblue-500 mb-1 flex items-center gap-2"><Users className="h-4 w-4" />Calls by Closer</h3>
      <p className="text-xs text-grey-500 mb-4">Calls this period</p>
      <div className="flex items-center justify-center h-48 text-grey-400">
        <div className="text-center"><Users className="h-8 w-8 mx-auto mb-2 text-grey-300" /><p>No team performance data available</p></div>
      </div>
    </div>
  );

  const chartData = data.map(m => ({ name:m.name||'Unknown', calls:m.calls||0, deals:m.deals||0, appointments:m.appointments||0, deal_value:m.deal_value||0, showed:m.showed||0, no_show:m.no_show||0 }));
  const totals    = chartData.reduce((a,m) => ({ calls:a.calls+m.calls, deals:a.deals+m.deals, appointments:a.appointments+m.appointments, deal_value:a.deal_value+m.deal_value }), { calls:0, deals:0, appointments:0, deal_value:0 });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active||!payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white p-3 border border-grey-200 rounded-lg shadow-lg">
        <p className="text-xs font-semibold text-darkblue-500 mb-2">{label}</p>
        <div className="space-y-1 text-xs">
          <p className="text-teal-700"><span className="font-medium">Calls:</span> {d.calls}</p>
          {d.deals>0 && <p className="text-brightblue-700"><span className="font-medium">Deals:</span> {d.deals}</p>}
          {d.deal_value>0 && <p className="text-brand-600"><span className="font-medium">Value:</span> ${(d.deal_value/1000).toFixed(1)}K</p>}
          {d.showed>0 && <p className="text-violet-700"><span className="font-medium">Show Rate:</span> {d.calls>0?Math.round(d.showed/d.calls*100):0}%</p>}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-grey-200 p-6">

      {/* Header */}
      <div className="flex justify-between items-start mb-5">
        <h3 className="text-sm font-semibold text-darkblue-500 flex items-center gap-2">
          <Users className="h-4 w-4" />
          Calls by Closer
          {chartData.length > 1 && (
            <div className="relative group ml-1">
              <Lightbulb className="h-4 w-4 text-yellow-500 cursor-pointer" />
              <div className="absolute left-0 mt-2 w-72 bg-white border border-grey-200 rounded-lg shadow-lg p-3 text-xs text-darkblue-500 z-10 hidden group-hover:block space-y-1">
                {(() => {
                  const top  = chartData.reduce((p,c) => p.calls>c.calls ? p : c);
                  const topD = chartData.reduce((p,c) => p.deals>c.deals ? p : c);
                  return (
                    <>
                      <div>📞 Most calls: <span className="font-semibold">{top.name}</span> — {top.calls}</div>
                      {totals.deals>0 && <div>🏆 Most deals: <span className="font-semibold">{topD.name}</span> — {topD.deals}</div>}
                      <div>📊 Avg: {(totals.calls/chartData.length).toFixed(1)} calls/person</div>
                      <div>💰 Pipeline: ${(totals.deal_value/1000).toFixed(1)}K</div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </h3>
        <div className="text-right text-xs text-grey-400">
          <div className="font-medium text-darkblue-500">{totals.calls} total calls</div>
          {filters && (
            <div>{filters.closer!=='all'&&`${filters.closer} • `}{filters.periods_back==='all'?'All periods':`${filters.periods_back} ${filters.aggregation_level} periods`}</div>
          )}
        </div>
      </div>

      {/* Bar chart — teal bars matching brand */}
      <div className="h-48 mb-5">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top:5, right:10, left:30, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#dcdeef" />
            <XAxis type="number" tick={{ fontSize:11, fill:'#152451' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize:11, fill:'#152451' }} width={110} interval={0} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="calls" fill="#009391" name="Calls" radius={[0,3,3,0]} opacity={0.85}
              label={{ position:'right', fontSize:11, fill:'#152451', fontWeight:600 }} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Individual breakdown */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-darkblue-500 mb-2 flex items-center gap-1">
          <TrendingUp className="h-3.5 w-3.5" />Individual Performance
        </h4>
        {[...chartData].sort((a,b)=>b.calls-a.calls).map((m, i) => {
          const conv = m.calls>0 ? ((m.deals/m.calls)*100).toFixed(1) : '0.0';
          const show = m.calls>0 ? ((m.showed/m.calls)*100).toFixed(1) : '0.0';
          return (
            <div key={m.name} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg hover:bg-grey-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <div>
                  <div className="text-xs font-semibold text-darkblue-500">{m.name}</div>
                  <div className="text-xs text-grey-500">
                    {m.calls} calls{m.deals>0&&` • ${m.deals} deals`}{m.deal_value>0&&` • $${(m.deal_value/1000).toFixed(1)}K`}
                  </div>
                </div>
              </div>
              <div className="text-right text-xs">
                {m.deals>0 && <div className="font-semibold text-darkblue-500">{conv}% conv.</div>}
                {m.showed>0 && <div className="text-grey-400">{show}% show</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Single performer note */}
      {chartData.length===1 && (
        <div className="mt-4 p-3 bg-brightblue-50 border border-brightblue-200 rounded-lg">
          <div className="text-xs text-brightblue-700">
            <span className="font-semibold">Showing: {chartData[0].name}</span>
            <div className="mt-0.5">
              {chartData[0].calls} calls{chartData[0].deals>0&&` • ${chartData[0].deals} deals`}
              {chartData[0].calls>0&&chartData[0].deals>0&&` • ${((chartData[0].deals/chartData[0].calls)*100).toFixed(1)}% conversion`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPerformanceSection;